import { SearchResultMatch } from "@app/analytics/types";
import { getUniqueKey } from "@app/analytics/utils";
import { UnauthorizedVideoRecording, VideoRecording } from "@app/common/types";

interface TextChunk {
  /** Unique key for mapped components */
  key: string;
  value: string;
  isMatch: boolean;
}

export const MAX_VISIBLE_MATCHES = 3;
export const MATCH_START_DELIMITER = "{{";
export const MATCH_END_DELIMITER = "}}";

const DELIMITER_MATCH_REGEX = /[^{{]+(?=}})/;

/**
 * Returns the value inside the match delimiters
 * (e.g., "{{foo}}" => "foo")
 * */
export const getMatchValue = (value: string) =>
  value.match(DELIMITER_MATCH_REGEX)?.[0] ?? value;

export const getMatchChunks = (text: string): TextChunk[] => {
  if (!text.includes(MATCH_START_DELIMITER)) {
    return [{ key: getUniqueKey(), value: text, isMatch: false }];
  }

  const textChunks: TextChunk[] = [];

  let remainingText = text;
  let matchStartIndex = remainingText.indexOf(MATCH_START_DELIMITER);
  let matchEndIndex =
    remainingText.indexOf(MATCH_END_DELIMITER) + MATCH_END_DELIMITER.length;

  while (matchStartIndex > -1) {
    if (matchStartIndex > 0) {
      textChunks.push({
        key: getUniqueKey(),
        value: remainingText.slice(0, matchStartIndex),
        isMatch: false,
      });
    }

    const matchValue = remainingText.slice(matchStartIndex, matchEndIndex);
    textChunks.push({
      key: getUniqueKey(),
      value: getMatchValue(matchValue),
      isMatch: true,
    });

    remainingText = remainingText.slice(matchEndIndex);
    matchStartIndex = remainingText.indexOf(MATCH_START_DELIMITER);
    matchEndIndex =
      remainingText.indexOf(MATCH_END_DELIMITER) + MATCH_END_DELIMITER.length;
  }

  if (remainingText.length) {
    textChunks.push({
      key: getUniqueKey(),
      value: remainingText,
      isMatch: false,
    });
  }

  return textChunks;
};

const MIN_DURATION = 2;

export const getClip = (
  match: SearchResultMatch,
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording
) => {
  if (
    !recordingInfo ||
    recordingInfo?.status !== "ready" ||
    !match.duration ||
    !match.startTime
  ) {
    // short-circuit if we don't have enough information to get the clip
    return;
  }

  const duration = Math.max(match.duration, MIN_DURATION);
  const clipStartSeconds = Math.max(
    match.startTime.diff(recordingInfo.startTime).as("seconds"),
    0
  );
  const clipEndSeconds = clipStartSeconds + duration;

  return {
    id: `${match.id}-${match.startTime.toMillis()}`,
    start: clipStartSeconds,
    end: clipEndSeconds,
    duration,
  };
};

export const getMatchPrefixAndSuffix = (match: SearchResultMatch) => {
  if (match.category === "turn") {
    const suffixShouldIncludeSpace = match.text.match(/\?|!|,|\.$/);
    return {
      prefix: '"...',
      suffix: suffixShouldIncludeSpace ? ' ..."' : '..."',
    };
  }

  const endsInSentenceEndingCharacter = match.text.match(/"|\?|!|\.$/);
  return {
    prefix: "...",
    suffix: endsInSentenceEndingCharacter ? "" : "...",
  };
};
